import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import MainLayout from '../components/layout/MainLayout';
import { storageService, userService } from '../services/supabase';
import { supabase } from '../services/supabase';

const Profile = () => {
  const { user, loading } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError(null);
    setSuccessMessage(null);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user) return;
    
    const file = e.target.files[0];
    
    // Verifica se o arquivo é uma imagem
    if (!file.type.match('image.*')) {
      setUploadError('Por favor, selecione uma imagem válida.');
      return;
    }
    
    // Tamanho máximo: 2MB
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('A imagem deve ter no máximo 2MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setSuccessMessage(null);
    
    try {
      const { data, error } = await storageService.uploadAvatar(file, user.id);
      
      if (error) {
        throw error;
      }
      
      if (!data || !data.publicUrl) {
        throw new Error('Erro ao obter URL da imagem.');
      }
      
      // Atualiza o avatar do usuário
      const { error: updateError } = await userService.updateUser(user.id, {
        avatar_url: data.publicUrl
      });
      
      if (updateError) {
        throw updateError;
      }
      
      // Força um recarregamento da página para atualizar o avatar
      window.location.reload();
      
      setSuccessMessage('Foto de perfil atualizada com sucesso!');
    } catch (err) {
      console.error('Erro ao fazer upload:', err);
      setUploadError('Erro ao fazer upload da imagem. Tente novamente.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    setSuccessMessage(null);
    
    // Validações
    if (passwordData.newPassword.length < 6) {
      setPasswordError('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('As senhas não coincidem.');
      return;
    }
    
    try {
      // Primeiro verificamos se a senha atual está correta
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user?.email || '',
        password: passwordData.currentPassword
      });
      
      if (signInError) {
        setPasswordError('Senha atual incorreta.');
        return;
      }
      
      // Atualiza a senha
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.newPassword
      });
      
      if (updateError) {
        throw updateError;
      }
      
      setSuccessMessage('Senha alterada com sucesso!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      setPasswordError('Erro ao alterar senha. Tente novamente.');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway mb-2">
          Meu <span className="text-vermelho-claro">Perfil</span>
        </h1>
        <p className="text-gray-300">
          Gerencie suas informações pessoais e preferências.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Coluna da esquerda - Informações do usuário */}
        <div className="bg-gray-900/50 p-8 rounded-lg">
          <div className="flex flex-col items-center mb-8">
            <div className="relative mb-4">
              <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-vermelho-claro/30 bg-black/30 flex items-center justify-center">
                {user?.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt="Avatar" 
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = "https://via.placeholder.com/150?text=Avatar";
                    }}
                  />
                ) : (
                  <div className="text-5xl text-gray-400 font-bold">
                    {user?.nome.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <label 
                htmlFor="avatar-upload" 
                className="absolute bottom-0 right-0 bg-vermelho-claro text-white p-2 rounded-full cursor-pointer hover:bg-red-700 transition"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </label>
              <input
                id="avatar-upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </div>
            
            {isUploading && (
              <div className="mb-2 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-vermelho-claro mr-2"></div>
                <span className="text-gray-300">Enviando...</span>
              </div>
            )}
            
            {uploadError && (
              <div className="mb-2 text-red-500 text-sm text-center">
                {uploadError}
              </div>
            )}
            
            <h2 className="text-xl font-bold text-white">{user?.nome}</h2>
            <p className="text-gray-400">{user?.email}</p>
            <div className="mt-2 inline-block bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded-full">
              {user?.tipo_usuario === 'admin' ? 'Administrador' : 'Usuário comum'}
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-6">
            <h3 className="text-lg font-semibold text-white mb-4">Informações da conta</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Nome</label>
                <div className="bg-gray-800 py-2 px-3 rounded text-white">{user?.nome}</div>
                <p className="text-xs text-gray-500 mt-1">O nome não pode ser alterado</p>
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1">Email</label>
                <div className="bg-gray-800 py-2 px-3 rounded text-white">{user?.email}</div>
                <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Coluna da direita - Alterar senha */}
        <div className="bg-gray-900/50 p-8 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-6">Alterar Senha</h3>
          
          <form onSubmit={handleChangePassword}>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1" htmlFor="currentPassword">
                  Senha Atual
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-vermelho-claro focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1" htmlFor="newPassword">
                  Nova Senha
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-vermelho-claro focus:border-transparent"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-400 text-sm mb-1" htmlFor="confirmPassword">
                  Confirmar Nova Senha
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="w-full bg-gray-800 border border-gray-700 rounded py-2 px-3 text-white focus:outline-none focus:ring-2 focus:ring-vermelho-claro focus:border-transparent"
                  required
                />
              </div>
              
              {passwordError && (
                <div className="text-red-500 text-sm">
                  {passwordError}
                </div>
              )}
              
              {successMessage && (
                <div className="text-green-500 text-sm">
                  {successMessage}
                </div>
              )}
              
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full bg-vermelho-claro hover:bg-red-700 text-white font-bold py-2 px-4 rounded transition duration-200"
                >
                  Alterar Senha
                </button>
              </div>
            </div>
          </form>
          
          <div className="mt-8 pt-6 border-t border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Dicas de segurança</h3>
            <ul className="text-gray-400 text-sm space-y-2 list-disc pl-5">
              <li>Use uma senha com pelo menos 8 caracteres</li>
              <li>Combine letras maiúsculas e minúsculas, números e símbolos</li>
              <li>Evite usar a mesma senha em diferentes serviços</li>
              <li>Altere sua senha periodicamente</li>
            </ul>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Profile; 